import type { Quad } from './geometry.js';
import type { Photo } from './pipeline.js';
import type { ScanFilter } from './render.js';

/**
 * SCAN-008 — a scan that is not finished yet, kept where a closed tab cannot
 * reach it.
 *
 * Photographing eleven pages of a contract takes a couple of minutes of
 * standing over a desk, and until now all of it lived in one React component:
 * a mis-tapped ✕, a phone that decided to reload the tab while the camera was
 * open, a notification followed by a return to a browser that had discarded the
 * page — any of them and the whole stack was gone with nothing to say about it.
 *
 * So the pages are written down as they are taken. IndexedDB rather than
 * `localStorage` because what is being kept is half a megabyte of JPEG per
 * page, which is not a string and is over the quota by page ten either way.
 *
 * Nothing here ever throws. A browser in private mode, a device out of space, a
 * schema from a version of this that no longer exists: every one of them means
 * the scan is not backed up, and none of them means the person cannot scan.
 */

const DB_NAME = 'worki-scan';
const STORE = 'drafts';
/**
 * Which unfinished scan. One per kind of scan rather than one in all: the
 * document library and the signing screen share `current`, as they always
 * have, and the invoices' quick scan (section 3) keeps its own, so photographs
 * of receipts never come back as the pages of a contract, or the other way round.
 */
export const DRAFT_KEY = 'current';
export const INVOICE_DRAFT_KEY = 'invoices';

/** Long enough to survive a lunch break, short enough that last month's
 *  abandoned scan is not offered back as though it were unfinished business. */
const KEEP_FOR_MS = 7 * 24 * 60 * 60 * 1000;

export interface DraftPage {
  id: string;
  /** The photograph, exactly as the scanner keeps it. Null for a picked PDF. */
  photo: Photo | null;
  file: File | null;
  quad: Quad;
  rotation: number;
}

export interface ScanDraft {
  pages: DraftPage[];
  filter: ScanFilter;
  /** What the scanner typed to call it, if they got that far. Optional so a
   *  draft written before this field existed still restores. */
  name?: string;
  savedAt: number;
}

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, 1);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

/** Runs one thing against the store and closes the connection either way. */
async function inStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const database = await open();
  if (!database) return null;
  try {
    return await new Promise<T | null>((resolve) => {
      const transaction = database.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => resolve((request.result as T) ?? null);
      request.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
    });
  } catch {
    return null;
  } finally {
    database.close();
  }
}

/** The scan as it stands. Called on every change, so it overwrites rather than
 *  accumulating: there is one unfinished scan, not a history of them. */
export async function saveDraft(pages: DraftPage[], filter: ScanFilter, name = '', key = DRAFT_KEY): Promise<void> {
  if (pages.length === 0) {
    await clearDraft(key);
    return;
  }
  const draft: ScanDraft = { pages, filter, name, savedAt: Date.now() };
  await inStore('readwrite', (store) => store.put(draft, key));
}

/** The scan that was left unfinished, if there is one worth offering back. */
export async function loadDraft(key = DRAFT_KEY): Promise<ScanDraft | null> {
  const draft = await inStore<ScanDraft>('readonly', (store) => store.get(key));
  if (!draft?.pages?.length) return null;
  if (!Number.isFinite(draft.savedAt) || Date.now() - draft.savedAt > KEEP_FOR_MS) {
    await clearDraft(key);
    return null;
  }
  // A page whose photograph did not survive the trip is dropped rather than
  // restored as a blank: the file it would produce is a blank page in a PDF.
  const pages = draft.pages.filter((page) => page.photo?.blob instanceof Blob || page.file instanceof Blob);
  return pages.length ? { ...draft, pages } : null;
}

export async function clearDraft(key = DRAFT_KEY): Promise<void> {
  await inStore('readwrite', (store) => store.delete(key));
}
